import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  speakText,
  formatSpeechMessage,
  initializeVoices,
  stopSpeaking
} from '../../../helpers/textToSpeech';

const SPEAK_THROTTLE_MS = 1500;
const MOUNT_ANNOUNCE_DELAY_MS = 2000;

const getItemKey = item => {
  if (!item || !item.product_id) return null;
  return `${item.product_id}-${item.qty_remaining || 0}`;
};

export const useSpeakLocation = () => {
  const voicePackingEnabled = useSelector(
    state => state.user?.settings?.data?.voice_packing
  );

  return useCallback(
    locationText => {
      if (!voicePackingEnabled) return;
      if (!locationText || typeof locationText !== 'string') return;
      speakText(`Location ${locationText}`);
    },
    [voicePackingEnabled]
  );
};

const useSpeakOnItemChange = () => {
  const suggestedItem = useSelector(
    state => state.scanPackScanning.currentOrder?.unscanned_items?.[0] || null
  );
  const voicePackingEnabled = useSelector(
    state => state.user?.settings?.data?.voice_packing
  );

  const lastItemKeyRef = useRef(null);
  const lastProductIdRef = useRef(null);
  const lastSpeakTimeRef = useRef(0);
  const hasAnnouncedOnMountRef = useRef(false);
  const mountTimerRef = useRef(null);
  const suggestedItemRef = useRef(suggestedItem);
  suggestedItemRef.current = suggestedItem;

  useEffect(() => {
    initializeVoices();
    return () => {
      clearTimeout(mountTimerRef.current);
      stopSpeaking();
    };
  }, []);

  const speakItem = useCallback(async (item, isItemChange) => {
    try {
      if (!item) return;

      const now = Date.now();
      if (now - lastSpeakTimeRef.current < SPEAK_THROTTLE_MS) return;

      const message = await formatSpeechMessage(item, null, isItemChange);

      if (message) {
        lastSpeakTimeRef.current = now;
        await speakText(message);
      }
    } catch (error) {
      console.error('Error speaking suggested item:', error);
    }
  }, []);

  useEffect(() => {
    const itemKey = getItemKey(suggestedItem);

    if (!voicePackingEnabled || !itemKey) {
      lastItemKeyRef.current = itemKey;
      lastProductIdRef.current = suggestedItem?.product_id || null;
      return;
    }

    if (itemKey === lastItemKeyRef.current) return;

    const isItemChange = lastProductIdRef.current !== suggestedItem.product_id;

    lastItemKeyRef.current = itemKey;
    lastProductIdRef.current = suggestedItem.product_id;

    if (!hasAnnouncedOnMountRef.current) {
      hasAnnouncedOnMountRef.current = true;
      mountTimerRef.current = setTimeout(() => {
        speakItem(suggestedItemRef.current, true);
      }, MOUNT_ANNOUNCE_DELAY_MS);
      return;
    }

    clearTimeout(mountTimerRef.current);
    speakItem(suggestedItem, isItemChange);
  }, [suggestedItem, voicePackingEnabled, speakItem]);

  return { voicePackingEnabled };
};

export default useSpeakOnItemChange;
